import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, TrendingUp, Users, MapPin, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

function AnimatedCounter({ target, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const animate = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
}

const CLIENT_INFO = [
  { icon: Building2, label: "Settore", value: "GDO - Hard Discount" },
  { icon: TrendingUp, label: "Fatturato", value: "1,5 mld €" },
  { icon: Users, label: "Dipendenti", value: "4.200" },
  { icon: MapPin, label: "Punti vendita", value: "+570" },
];

const HERO_STATS = [
  { value: 900, label: "Assessment completati" },
  { value: 90, label: "Profili alto potenziale" },
  { value: 47, suffix: "%", label: "Pronti da subito" },
];

const INS_LOGO_URL = "https://customer-assets.emergentagent.com/job_2b924b31-3dec-4508-86fc-f552bf10515d/artifacts/o60ehn61_images%20%281%29.png";

export default function HeroSection() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-[var(--sv-surface)]" />
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.06]"
        style={{ background: "radial-gradient(circle, #FFB74B, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-[-100px] w-[500px] h-[500px] rounded-full opacity-[0.04]"
        style={{ background: "radial-gradient(circle, #4B4DF7, transparent 70%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="pt-4"
          >
            <Badge
              data-testid="hero-badge"
              className="mb-6 bg-[#4B4DF7]/8 text-[var(--sv-primary)] border-[#4B4DF7]/20 hover:bg-[#4B4DF7]/8 font-semibold text-xs tracking-wide uppercase px-3 py-1"
            >
              Case Study
            </Badge>

            <h1
              data-testid="hero-title"
              className="font-['Inter_Tight'] text-4xl sm:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-[1.1] text-[var(--sv-secondary)]"
            >
              Come In's Mercato ha costruito una{" "}
              <span className="text-[var(--sv-primary)]">pipeline interna</span>{" "}
              di Store Manager
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed max-w-xl"
            >
              La crescita della rete dipende dalla capacità di avere figure manageriali formate nel momento in cui servono. Con Skillvue, In's ha trasformato la gestione del talento da reattiva a predittiva.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://www.skillvue.ai/contact-us"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta"
              >
                <Button className="bg-[var(--sv-primary)] hover:bg-[var(--sv-primary-hover)] text-white rounded-md h-11 px-6 text-sm font-semibold gap-2">
                  Contattaci <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
              <button
                data-testid="hero-scroll-btn"
                onClick={() => document.querySelector("#sfida")?.scrollIntoView({ behavior: "smooth" })}
                className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[var(--sv-primary)] transition-colors px-4"
              >
                Scopri di più <ArrowDown className="w-4 h-4" />
              </button>
            </div>

            {/* Logos row */}
            <div className="mt-10 flex items-end gap-4" data-testid="hero-logos">
              <div className="flex items-center gap-2">
                <img
                  src="https://cdn.prod.website-files.com/63eb9c5c0665608db409b4df/66cdde23b308922774c4b3e0_logomark.svg"
                  alt="Skillvue"
                  className="h-8"
                />
                <img
                  src="https://cdn.prod.website-files.com/63eb9c5c0665608db409b4df/66d02c8992a92ee8b5595df6_Skillvue_logo_Type_Black.svg"
                  alt="Skillvue"
                  className="h-5 opacity-50"
                />
              </div>
              <span className="text-slate-300 text-lg font-light pb-1">&times;</span>
              <img
                src={INS_LOGO_URL}
                alt="In's Mercato"
                className="h-12 rounded object-contain translate-y-3"
                data-testid="ins-mercato-logo"
              />
            </div>
          </motion.div>

          {/* Right - Client Profile Card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card
              data-testid="hero-client-card"
              className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
            >
              {/* Card header with In's logo */}
              <div className="p-6 pb-4 flex items-center gap-4 border-b border-slate-100">
                <img
                  src={INS_LOGO_URL}
                  alt="In's Mercato"
                  className="h-12 w-12 rounded-lg object-cover"
                />
                <div>
                  <h3 className="font-['Inter_Tight'] font-bold text-lg text-[var(--sv-secondary)]">
                    In's Mercato
                  </h3>
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">
                    Scheda Cliente
                  </p>
                </div>
              </div>

              {/* Client info grid */}
              <div className="p-6 grid grid-cols-2 gap-4">
                {CLIENT_INFO.map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.4 + i * 0.1 }}
                    data-testid={`hero-client-info-${i}`}
                    className="flex items-start gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#4B4DF7]/6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-[var(--sv-primary)]" />
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">
                        {item.label}
                      </p>
                      <p className="text-sm font-bold text-[var(--sv-secondary)] font-['Inter_Tight']">
                        {item.value}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

            </Card>

            {/* Video Interview */}
            <div className="mt-4 bg-[#1a1a2e] rounded-2xl overflow-hidden shadow-lg">
              <video
                data-testid="full-interview-video"
                className="w-full aspect-video object-cover"
                controls
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                src="/full-interview.mp4"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
